/**
 * css_color.ts — a CSS colour as NUMBERS, for a canvas (the gaps wave, round 2, 2026-09-28).
 *
 * A canvas `fillStyle` parses fewer colour syntaxes than CSS does: `color-mix()` (and `color(srgb …)` on older engines)
 * is silently ignored there — the assignment is dropped and the fill keeps its PREVIOUS value, so every heat-map cell
 * painted the colour of the one before it. A canvas UI resolves its theme colours here instead:
 *   canvasColor(themeColor('--ax-good'), 0.8)   → 'rgba(158, 196, 138, 0.8)'
 *   mixCanvasColor(lo, hi, t)                    → 'rgb(…)', blended in sRGB like color-mix(in srgb …)
 * Hex, rgb()/rgba() (comma or space syntax, % or numbers) and color(srgb …) are parsed here, pure; anything else
 * (a named colour, hsl(), a color-mix()) is handed to the browser's own computed style on a probe element and parsed
 * again — the browser resolves every syntax it can paint. The output is ALWAYS plain rgb()/rgba(), which every canvas
 * parses. A value nothing can read falls back to the caller's fallback.
 */

export type Rgba = [number, number, number, number];

const clamp255 = (v: number): number => Math.max(0, Math.min(255, Math.round(v)));
const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

function channel(tok: string, scale: number): number | null {
  const t = tok.trim();
  if (t === '' || t === 'none') return t === 'none' ? 0 : null;
  if (t.endsWith('%')) { const v = Number(t.slice(0, -1)); return Number.isFinite(v) ? (v / 100) * scale : null; }
  const v = Number(t);
  return Number.isFinite(v) ? v : null;
}

/** Parse the syntaxes a script can read without a browser: #rgb #rgba #rrggbb #rrggbbaa, rgb()/rgba(), color(srgb …). */
export function parseCssColor(input: string): Rgba | null {
  const s = String(input ?? '').trim().toLowerCase();
  if (!s) return null;
  let m = /^#([0-9a-f]{3,8})$/.exec(s);
  if (m) {
    const h = m[1];
    if (h.length === 3 || h.length === 4) {
      const c = h.split('').map((x) => parseInt(x + x, 16));
      return [c[0], c[1], c[2], h.length === 4 ? c[3] / 255 : 1];
    }
    if (h.length === 6 || h.length === 8) {
      const c = [0, 2, 4, 6].slice(0, h.length / 2).map((i) => parseInt(h.slice(i, i + 2), 16));
      return [c[0], c[1], c[2], h.length === 8 ? c[3] / 255 : 1];
    }
    return null;
  }
  m = /^rgba?\((.*)\)$/.exec(s);
  if (m) {
    const body = m[1].trim();
    let parts: string[];
    let alphaTok: string | undefined;
    if (body.includes(',')) { parts = body.split(','); if (parts.length === 4) alphaTok = parts.pop(); }
    else { const [rgb, a] = body.split('/'); parts = rgb.trim().split(/\s+/); alphaTok = a; }
    if (parts.length !== 3) return null;
    const c = parts.map((p) => channel(p, 255));
    if (c.some((v) => v === null)) return null;
    const a = alphaTok === undefined ? 1 : channel(alphaTok, 1);
    if (a === null) return null;
    return [clamp255(c[0]!), clamp255(c[1]!), clamp255(c[2]!), clamp01(a)];
  }
  m = /^color\(\s*srgb\s+([^)]*)\)$/.exec(s);
  if (m) {
    const [rgb, aTok] = m[1].split('/');
    const parts = rgb.trim().split(/\s+/);
    if (parts.length !== 3) return null;
    const c = parts.map((p) => channel(p, 1));
    if (c.some((v) => v === null)) return null;
    const a = aTok === undefined ? 1 : channel(aTok, 1);
    if (a === null) return null;
    return [clamp255(c[0]! * 255), clamp255(c[1]! * 255), clamp255(c[2]! * 255), clamp01(a)];
  }
  return null;
}

let probe: HTMLElement | null = null;
const resolved = new Map<string, Rgba | null>();

/** Any CSS colour → numbers: parsed, else resolved by the browser (computed style on a hidden probe), else null. */
export function resolveCssColor(input: string): Rgba | null {
  const direct = parseCssColor(input);
  if (direct) return direct;
  const key = String(input ?? '').trim();
  if (resolved.has(key)) return resolved.get(key) ?? null;
  let out: Rgba | null = null;
  try {
    if (typeof document !== 'undefined' && document.body) {
      if (!probe || !probe.isConnected) {
        probe = document.createElement('span');
        probe.style.cssText = 'position:absolute;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none';
        document.body.appendChild(probe);
      }
      probe.style.color = '';
      probe.style.color = key;
      if (probe.style.color !== '') out = parseCssColor(getComputedStyle(probe).color);
    }
  } catch { out = null; }
  if (resolved.size > 256) resolved.clear();
  resolved.set(key, out);
  return out;
}

const fmt = (c: Rgba, alpha: number): string => {
  const a = clamp01(c[3] * alpha);
  return a >= 1 ? `rgb(${c[0]}, ${c[1]}, ${c[2]})` : `rgba(${c[0]}, ${c[1]}, ${c[2]}, ${Math.round(a * 1000) / 1000})`;
};

/** A colour a canvas will paint: plain rgb()/rgba(), with an extra alpha multiplied in. */
export function canvasColor(input: string, alpha = 1, fallback = ''): string {
  const c = resolveCssColor(input) ?? parseCssColor(fallback) ?? [0, 0, 0, 1];
  return fmt(c, alpha);
}

/** `a` blended toward `b` by t (0..1) in sRGB — what `color-mix(in srgb, b t%, a)` means — as plain rgb()/rgba(). */
export function mixCanvasColor(a: string, b: string, t: number, fallback = ''): string {
  const pa = resolveCssColor(a) ?? parseCssColor(fallback) ?? [0, 0, 0, 1];
  const pb = resolveCssColor(b) ?? parseCssColor(fallback) ?? [0, 0, 0, 1];
  const k = clamp01(Number.isFinite(t) ? t : 0);
  const c: Rgba = [clamp255(pa[0] + (pb[0] - pa[0]) * k), clamp255(pa[1] + (pb[1] - pa[1]) * k), clamp255(pa[2] + (pb[2] - pa[2]) * k), pa[3] + (pb[3] - pa[3]) * k];
  return fmt(c, 1);
}
